import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Typography, Tooltip, IconButton, Tabs, Tab } from '@mui/material';
import { Editor, Frame, Canvas, Element, useEditor } from '@craftjs/core';
import { TextT, Minus, Image, Info, Plus, PencilSimple, Trash, Columns } from '@phosphor-icons/react';
import { blocksToCraftJson, craftJsonToBlocks, filterLayoutBlocks } from '../../../utils/proposalCraftUtils';
import { getLineItemTotal, getUnitPriceWithTax } from '../../../utils/taxCalculations';
import {
  HeadingBlock,
  ParagraphBlock,
  ImageBlock,
  DividerBlock,
  RowBlock,
  ColumnBlock
} from './craft/ProposalCraftBlocks';
import AgreementAdditionalInformationPanel from './AgreementAdditionalInformationPanel';

const BLOCK_TYPES = [
  { type: 'row', label: 'Row', icon: <Columns size={20} style={{ transform: 'rotate(90deg)' }} /> },
  { type: 'heading', label: 'Heading', icon: <TextT size={20} /> },
  { type: 'paragraph', label: 'Paragraph', icon: <TextT size={20} /> },
  { type: 'image', label: 'Image', icon: <Image size={20} /> },
  { type: 'divider', label: 'Divider', icon: <Minus size={20} /> }
];

const RESOLVER = {
  Canvas,
  Element,
  HeadingBlock,
  ParagraphBlock,
  ImageBlock,
  DividerBlock,
  RowBlock,
  ColumnBlock
};

const BLOCK_DEFAULTS = {
  heading: { text: '', level: 2 },
  paragraph: { text: '' },
  image: { url: '', alt: '' },
  divider: {},
  row: { columns: 2 },
  column: { width: 50 }
};

function MetaFieldHint({ title, sx: iconSx = {} }) {
  return (
    <Tooltip title={title} placement="top" arrow>
      <IconButton size="small" aria-label="More information" sx={{ color: 'text.secondary', ml: 0.5, ...iconSx }}>
        <Info size={18} />
      </IconButton>
    </Tooltip>
  );
}

function formatMoney(amount, currency = 'GBP') {
  const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
}

function lineItemRowTotal(item) {
  return getLineItemTotal(item);
}

function scheduleSendLabel(s) {
  if (s.dueType === 'immediate') return 'Sent immediately on acceptance';
  if (s.dueType === 'days_from_accept') return `Sent ${s.dueValue || 0} days after acceptance`;
  if (s.dueType === 'days_before_event') return `Sent ${s.dueValue || 0} days before wedding`;
  return '';
}

const sectionTitleSx = {
  fontSize: { xs: '1.125rem', sm: '1.25rem' },
  fontWeight: 600,
  lineHeight: 1.3
};

const addBtnSx = { textTransform: 'none', fontWeight: 500 };
const addLineItemBtnSx = {
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' }
};

function tabLabel(step, label) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'divider',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          lineHeight: 1
        }}
      >
        {step}
      </Box>
      <span>{label}</span>
    </Box>
  );
}

function ProposalPricingPanel({
  lineItems,
  currency = 'GBP',
  onAddLineItem,
  onEditLineItem,
  onRemoveLineItem
}) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const hasAnyLine = items.length > 0;
  const total = items.reduce((sum, item) => sum + lineItemRowTotal(item), 0);
  const showLineActions = typeof onAddLineItem === 'function';

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', flex: 1, overflow: 'auto', minHeight: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Shown on the published agreement after your layout blocks.
      </Typography>
      <Typography variant="h6" component="h3" sx={{ ...sectionTitleSx, mb: 0.5 }}>
        Services & pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '42rem' }}>
        Line items and totals shown to the couple on the published agreement.
      </Typography>
      {!hasAnyLine ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {showLineActions ? 'Add at least one line item to set pricing.' : 'No line items.'}
          </Typography>
          {showLineActions && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<Plus size={16} />}
              onClick={onAddLineItem}
              sx={addLineItemBtnSx}
            >
              Add line item
            </Button>
          )}
        </>
      ) : (
        <>
          <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
            {items.map((item, idx) => {
              const desc = (item.description || '').trim() || '—';
              const qty = Number(item.quantity) || 1;
              const unitWithTax = getUnitPriceWithTax(item);
              const lineTot = lineItemRowTotal(item);
              return (
                <Box
                  key={idx}
                  component="li"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {desc}
                    {item.isOptional ? ' (optional)' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                    {qty}× {formatMoney(unitWithTax, currency)}
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(lineTot, currency)}
                  </Typography>
                  {(onEditLineItem || onRemoveLineItem) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {onEditLineItem && (
                        <IconButton size="small" aria-label="Edit line item" onClick={() => onEditLineItem(idx)}>
                          <PencilSimple size={18} />
                        </IconButton>
                      )}
                      {onRemoveLineItem && (
                        <IconButton size="small" aria-label="Remove line item" onClick={() => onRemoveLineItem(idx)} color="error">
                          <Trash size={18} />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1
            }}
          >
            {showLineActions && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Plus size={16} />}
                onClick={onAddLineItem}
                sx={addLineItemBtnSx}
              >
                Add line item
              </Button>
            )}
            <Typography variant="body2" fontWeight={600} sx={{ ml: showLineActions ? 'auto' : 0 }}>
              Total: {formatMoney(Math.round(total * 100) / 100, currency)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Totals include VAT where set on a line item.
          </Typography>
        </>
      )}
    </Box>
  );
}

function ProposalInvoicingPanel({
  billingSchedule,
  currency = 'GBP',
  onAddSchedule,
  onEditSchedule,
  onRemoveSchedule
}) {
  const schedules = Array.isArray(billingSchedule) ? billingSchedule : [];
  const showScheduleActions = typeof onAddSchedule === 'function';

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', flex: 1, overflow: 'auto', minHeight: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Shown on the published agreement after pricing.
      </Typography>
      <Typography variant="h6" component="h3" sx={{ ...sectionTitleSx, mb: 0.5 }}>
        Billing schedule
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '42rem' }}>
        When each invoice is sent—on acceptance, after acceptance, or before the wedding.
      </Typography>
      {schedules.length === 0 ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {showScheduleActions ? 'Add when each invoice should be sent.' : 'No billing rows.'}
          </Typography>
          {showScheduleActions && (
            <Button size="small" variant="text" color="inherit" startIcon={<Plus size={16} />} onClick={onAddSchedule} sx={addBtnSx}>
              Add schedule item
            </Button>
          )}
        </>
      ) : (
        <>
          <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
            {schedules.map((s, idx) => {
              const send = scheduleSendLabel(s);
              const label = (s.label || '').trim() || '—';
              return (
                <Box
                  key={idx}
                  component="li"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">
                      <strong>{label}</strong>
                      {' — '}
                      {s.amountType === 'percentage' ? `${s.value ?? 0}% of total` : formatMoney(parseFloat(s.value) || 0, currency)}
                    </Typography>
                    {send && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {send}
                      </Typography>
                    )}
                  </Box>
                  {(onEditSchedule || onRemoveSchedule) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {onEditSchedule && (
                        <IconButton size="small" aria-label="Edit schedule row" onClick={() => onEditSchedule(idx)}>
                          <PencilSimple size={18} />
                        </IconButton>
                      )}
                      {onRemoveSchedule && (
                        <IconButton size="small" aria-label="Remove schedule row" onClick={() => onRemoveSchedule(idx)} color="error">
                          <Trash size={18} />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          {showScheduleActions && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="text" color="inherit" startIcon={<Plus size={16} />} onClick={onAddSchedule} sx={addBtnSx}>
                Add schedule item
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function ProposalTermsPanel({ termsText, onEditTerms }) {
  const termsTrim = (termsText || '').trim();
  const showTermsEdit = typeof onEditTerms === 'function';

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', flex: 1, overflow: 'auto', minHeight: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Shown on the published agreement after invoicing.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h6" component="h3" sx={sectionTitleSx}>
          Terms & conditions
        </Typography>
        {termsTrim && showTermsEdit && (
          <IconButton size="small" aria-label="Edit terms" onClick={onEditTerms} sx={{ color: 'text.secondary' }}>
            <PencilSimple size={18} />
          </IconButton>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '42rem' }}>
        Terms, cancellation policy, or other text shown after your agreement content.
      </Typography>
      {termsTrim ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: 1.5,
            maxHeight: 280,
            overflow: 'auto'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {termsText}
          </Typography>
        </Box>
      ) : (
        <Box
          role={showTermsEdit ? 'button' : undefined}
          tabIndex={showTermsEdit ? 0 : undefined}
          onClick={showTermsEdit ? onEditTerms : undefined}
          onKeyDown={
            showTermsEdit
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') onEditTerms();
                }
              : undefined
          }
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: 1.5,
            color: 'text.secondary',
            cursor: showTermsEdit ? 'pointer' : 'default'
          }}
        >
          <Typography variant="body2">
            No terms added yet.
            {showTermsEdit ? ' Click here to add terms.' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function BlockPalette() {
  const { connectors } = useEditor();
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Blocks
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Drag to add
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
      {BLOCK_TYPES.map((t) => {
        const props = BLOCK_DEFAULTS[t.type] || {};
        const Component =
          t.type === 'heading'
            ? HeadingBlock
            : t.type === 'paragraph'
              ? ParagraphBlock
              : t.type === 'image'
                  ? ImageBlock
                  : t.type === 'row'
                    ? RowBlock
                    : DividerBlock;
        return (
          <Box
            key={t.type}
            ref={(ref) => {
                if (!ref || !connectors?.create) return;
                if (t.type === 'row') {
                  connectors.create(ref, <Element is={Component} canvas {...props} />);
                  return;
                }
                connectors.create(ref, React.createElement(Component, props));
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 1,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              cursor: 'grab',
              fontFamily: 'inherit',
              fontSize: '0.8125rem',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 0.75,
                bgcolor: 'grey.100',
                p: 0.5,
                color: 'common.black',
                flexShrink: 0
              }}
            >
              {t.icon}
            </Box>
            <span>{t.label}</span>
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}

function EditorBlocksSync({ editorRef }) {
  const { query } = useEditor();
  useEffect(() => {
    if (!editorRef) return;
    editorRef.current = {
      getBlocks: () => {
        try {
          const serialized = query.serialize();
          const blocks = serialized ? craftJsonToBlocks(serialized) : [];
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('[ProposalCraftEditor] getBlocks:', blocks.length);
          }
          return blocks;
        } catch {
          return [];
        }
      }
    };
    return () => {
      editorRef.current = null;
    };
  }, [editorRef, query]);
  return null;
}

/**
 * @param {object} props
 * @param {import('react').ReactNode} [props.metaSlot] — fields above the canvas (e.g. template name)
 * @param {import('react').ReactNode} [props.recipientSlot] — recipient and message controls
 * @param {boolean} [props.showRecipientTab] — when false, hides the Recipient tab (e.g. template builder)
 * @param {boolean} [props.showInvoicingTab] — when false, hides invoicing controls/tabs
 * @param {Array} [props.additionalInformationFields]
 * @param {function} [props.onAdditionalInformationFieldsChange]
 * @param {string} [props.termsText]
 * @param {Array} [props.lineItems]
 * @param {Array} [props.billingSchedule]
 * @param {string} [props.currency]
 * @param {function} [props.onAddLineItem]
 * @param {function} [props.onEditLineItem]
 * @param {function} [props.onRemoveLineItem]
 * @param {function} [props.onAddSchedule]
 * @param {function} [props.onEditSchedule]
 * @param {function} [props.onRemoveSchedule]
 * @param {function} [props.onEditTerms]
 */
function ProposalCraftEditor({
  blocks,
  onBlocksChange,
  editorRef,
  metaSlot = null,
  recipientSlot = null,
  showRecipientTab = true,
  showInvoicingTab = true,
  additionalInformationFields = [],
  onAdditionalInformationFieldsChange,
  termsText = '',
  lineItems = [],
  billingSchedule = [],
  currency = 'GBP',
  onAddLineItem,
  onEditLineItem,
  onRemoveLineItem,
  onAddSchedule,
  onEditSchedule,
  onRemoveSchedule,
  onEditTerms
}) {
  const [craftTab, setCraftTab] = useState(0);
  /** Sync form only when parsed layout blocks change — Craft fires onNodesChange on many store updates; pushing React state every time breaks HTML5 drag from the palette. */
  const lastSyncedSerializedNodesRef = useRef('');

  const layoutBlocks = useMemo(() => filterLayoutBlocks(blocks || []), [blocks]);

  const frameData = useMemo(() => {
    if (!layoutBlocks?.length) return undefined;
    try {
      return blocksToCraftJson(layoutBlocks);
    } catch {
      return undefined;
    }
  }, [layoutBlocks]);

  const handleNodesChange = useCallback(
    (query) => {
      try {
        if (!query || typeof query.serialize !== 'function') return;
        const serialized = query.serialize();
        if (serialized == null || serialized === '') return;
        const next = craftJsonToBlocks(serialized);
        const snapshot = JSON.stringify(next);
        if (snapshot === lastSyncedSerializedNodesRef.current) return;
        lastSyncedSerializedNodesRef.current = snapshot;
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('[ProposalCraftEditor] onNodesChange blocks:', next.length);
        }
        onBlocksChange(next);
      } catch {
        // Ignore
      }
    },
    [onBlocksChange]
  );

  const handleTabChange = useCallback((_e, next) => {
    setCraftTab(next);
  }, []);

  const termsTabIndex = showInvoicingTab ? 3 : 2;
  const additionalInfoTabIndex = termsTabIndex + 1;
  const recipientTabIndex = additionalInfoTabIndex + 1;
  const termsStep = showInvoicingTab ? 4 : 3;
  const additionalInfoStep = termsStep + 1;
  const recipientStep = additionalInfoStep + 1;

  return (
    <Editor resolver={RESOLVER} onNodesChange={handleNodesChange} enabled={true}>
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          width: '100%',
          maxWidth: 'none',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0
        }}
      >
        {editorRef && <EditorBlocksSync editorRef={editorRef} />}

        {metaSlot ? (
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: { xs: 2, md: 2.5 },
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            {metaSlot}
          </Box>
        ) : null}

        <Tabs
          value={craftTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Agreement editor sections"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            minHeight: 48,
            px: 0,
            '& .MuiTabs-scroller': {
              overflowX: 'auto !important'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
              py: 1.25,
              px: 2.5
            }
          }}
        >
          <Tab label={tabLabel(1, 'Agreement editor')} id="proposal-craft-tab-0" aria-controls="proposal-craft-tabpanel-0" />
          <Tab label={tabLabel(2, 'Pricing')} id="proposal-craft-tab-1" aria-controls="proposal-craft-tabpanel-1" />
          {showInvoicingTab ? (
            <Tab label={tabLabel(3, 'Invoicing')} id="proposal-craft-tab-2" aria-controls="proposal-craft-tabpanel-2" />
          ) : null}
          <Tab
            label={tabLabel(termsStep, 'Terms')}
            id={`proposal-craft-tab-${termsTabIndex}`}
            aria-controls={`proposal-craft-tabpanel-${termsTabIndex}`}
          />
          <Tab
            label={tabLabel(additionalInfoStep, 'Additional information')}
            id={`proposal-craft-tab-${additionalInfoTabIndex}`}
            aria-controls={`proposal-craft-tabpanel-${additionalInfoTabIndex}`}
          />
          {showRecipientTab ? (
            <Tab
              label={tabLabel(recipientStep, 'Recipient')}
              id={`proposal-craft-tab-${recipientTabIndex}`}
              aria-controls={`proposal-craft-tabpanel-${recipientTabIndex}`}
            />
          ) : null}
        </Tabs>

        <Box
          sx={{
            display: craftTab === 0 ? 'flex' : 'none',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
          id="proposal-craft-tabpanel-0"
          role="tabpanel"
          aria-labelledby="proposal-craft-tab-0"
        >
          <Box
            sx={{
              width: { xs: '100%', md: 168 },
              flexShrink: 0,
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}
          >
            <BlockPalette />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 320,
              bgcolor: 'background.paper'
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 2,
                overflow: 'auto'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minHeight: 280,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  p: 2,
                  pb: layoutBlocks.length > 0 ? 16 : 2
                }}
              >
                <Frame data={frameData}>
                  <Element is="div" id="proposal-canvas" canvas style={{ minHeight: 280 }} />
                </Frame>
                {layoutBlocks.length === 0 ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 1,
                      px: 2
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        textAlign: 'center'
                      }}
                    >
                      <Plus size={32} aria-hidden />
                      <Typography variant="body2">Drag block here</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 16,
                      right: 16,
                      bottom: 16,
                      py: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      color: 'text.secondary',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      zIndex: 1
                    }}
                    aria-hidden
                  >
                    <Plus size={24} aria-hidden />
                    <Typography variant="body2">Drag block here</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: craftTab === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
          id="proposal-craft-tabpanel-1"
          role="tabpanel"
          aria-labelledby="proposal-craft-tab-1"
        >
          <ProposalPricingPanel
            lineItems={lineItems}
            currency={currency}
            onAddLineItem={onAddLineItem}
            onEditLineItem={onEditLineItem}
            onRemoveLineItem={onRemoveLineItem}
          />
        </Box>

        {showInvoicingTab ? (
          <Box
            sx={{
              display: craftTab === 2 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
            id="proposal-craft-tabpanel-2"
            role="tabpanel"
            aria-labelledby="proposal-craft-tab-2"
          >
            <ProposalInvoicingPanel
              billingSchedule={billingSchedule}
              currency={currency}
              onAddSchedule={onAddSchedule}
              onEditSchedule={onEditSchedule}
              onRemoveSchedule={onRemoveSchedule}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            display: craftTab === termsTabIndex ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
          id={`proposal-craft-tabpanel-${termsTabIndex}`}
          role="tabpanel"
          aria-labelledby={`proposal-craft-tab-${termsTabIndex}`}
        >
          <ProposalTermsPanel termsText={termsText} onEditTerms={onEditTerms} />
        </Box>

        <Box
          sx={{
            display: craftTab === additionalInfoTabIndex ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
          id={`proposal-craft-tabpanel-${additionalInfoTabIndex}`}
          role="tabpanel"
          aria-labelledby={`proposal-craft-tab-${additionalInfoTabIndex}`}
        >
          <AgreementAdditionalInformationPanel
            fields={additionalInformationFields}
            onChange={onAdditionalInformationFieldsChange}
          />
        </Box>

        {showRecipientTab ? (
          <Box
            sx={{
              display: craftTab === recipientTabIndex ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
            id={`proposal-craft-tabpanel-${recipientTabIndex}`}
            role="tabpanel"
            aria-labelledby={`proposal-craft-tab-${recipientTabIndex}`}
          >
            <Box sx={{ p: 3, bgcolor: 'grey.50', flex: 1, overflow: 'auto', minHeight: 0 }}>
              {recipientSlot || (
                <Typography variant="body2" color="text.secondary">
                  Add recipient details.
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Editor>
  );
}

export { MetaFieldHint };
export default ProposalCraftEditor;
